import {
    UserResponse,
    Collection,
    UserCoverStyle,
    UserProfileStyle,
    PublicUserResponse,
    ProfileUserResponse,
    PublicCollectionResponse
} from "@authory-packages/core";
import {
    CollectionEditCoverPos,
    CollectionHeaderContent,
    CollectionHeaderMetadata,
    UserThemableCollectionDescription,
    UserThemableCollectionTitle
} from "./CollectionHeader.styles";
import { V3TertiaryButton } from "../Button";
import { V3TertiaryCTAColor } from "../Button/V3TertiaryCTA.types";
import { V3IconBin, V3IconImage } from "../Icons";
import { VerticalSpacing } from "../VerticalSpacing";
import { V4PHImageContainer } from "../ProfileHero/V4ProfileHero.styles";
import { getAuthoryColorScheme } from "../ProfileHero/V4ProfileHero.utils";
import { CreateCollectionHandler, CreateUpdateCollectionFormik } from "../CreateUpdateCollectionModal";
import { SidebarLayoutShowDesktop } from "../../layout/SideBarLayout/SidebarLayout.styles";
import { V3PublicAuthorHeader } from "../V3PublicAuthorHeader";
import { ContactUserFormik } from "../V3ContactUser";
import { V3LoaderSpinner } from "../Loader";
import { DefaultCollectionTooltip } from "../ReusableStyledComponents/DefaultCollectionTooltip";
import { GenericContentEditable } from "../GenericContentEditable";
import { getCoverInfo } from "../../utils/getCoverInfo";
import { theme } from "../../theme";
import { MediaEditingFormik } from "../V3MediaEditingModal";

interface CollectionHeaderProps {
    collection: Collection | PublicCollectionResponse;
    editMode?: boolean;
    onCollectionUpdateInfo?: CreateCollectionHandler;
    onEditCoverClick?: () => void;
    user: UserResponse | PublicUserResponse | ProfileUserResponse;
    onContactUserSubmit: (data: ContactUserFormik) => Promise<void>;
    onCoverModalsubmit?: (data: MediaEditingFormik) => Promise<void>;
    isStandaloneCollectionView?: boolean;
    useBorderRadius?: boolean;
    smartShareUrl?: string;
}

export const CollectionHeader = ({
    collection,
    editMode = false,
    user,
    onContactUserSubmit,
    onCollectionUpdateInfo,
    onCoverModalsubmit,
    onEditCoverClick,
    isStandaloneCollectionView = true,
    useBorderRadius = false,
    smartShareUrl
}: CollectionHeaderProps) => {
    const isSidebar = "profileStyle" in user ? user.profileStyle === UserProfileStyle.sidebar : false;
    const hasImage = collection.coverStyle === UserCoverStyle.image || collection.coverStyle === UserCoverStyle.unsplash;
    const hasColor = collection.coverStyle === UserCoverStyle.color && collection.coverColor;
    const { color } = getCoverInfo(collection);
    const colorVariant = getAuthoryColorScheme(collection.coverStyle as UserCoverStyle);

    const onCollectionLabelEditEnd = (value: string) => {
        const data: CreateUpdateCollectionFormik = {
            name: value
        };

        onCollectionUpdateInfo && onCollectionUpdateInfo(data);
    };

    const onCollectionDescriptionEditEnd = (value: string) => {
        const data: CreateUpdateCollectionFormik = {
            description: value
        };

        onCollectionUpdateInfo && onCollectionUpdateInfo(data);
    };

    return (
        <>
            <V4PHImageContainer
                $useImage={!!hasImage}
                $coverColor={hasColor ? collection.coverColor! : undefined}
                $desktopSrc={!!collection.coverImage.length ? collection.coverImage[0].url : undefined}
                $mobileSrc={collection.coverImage.length > 1 ? collection.coverImage[1].url : undefined}
                $enforceMinHeight={false}
                $isAboutPage={false}
                $profileStyle={UserProfileStyle.centered}
                $isPrivateView={editMode}
                $useBorderRadius={useBorderRadius}
            >
                {!editMode && (isStandaloneCollectionView || !isSidebar) && (
                    <V3PublicAuthorHeader
                        user={user}
                        onContactUserSubmit={onContactUserSubmit}
                        nonSticky
                        collection={collection}
                        showAboutPagesNav={!isStandaloneCollectionView}
                        customBackgroundColor={color}
                        smartShareUrl={smartShareUrl}
                    />
                )}
                <CollectionHeaderContent $colorVariant={colorVariant} $editMode={editMode} $viewType={collection.view}>
                    <CollectionHeaderMetadata>
                        <UserThemableCollectionTitle as="div" $colorVariant={colorVariant} $customBackgroundColor={color}>
                            <GenericContentEditable
                                colorVariant={colorVariant}
                                text={collection?.label}
                                editMode={editMode}
                                onEditCallback={onCollectionLabelEditEnd}
                                customBackgroundColor={color}
                            />
                        </UserThemableCollectionTitle>
                        {collection.default && (
                            <div>
                                <VerticalSpacing top={22}>
                                    <DefaultCollectionTooltip />
                                </VerticalSpacing>
                            </div>
                        )}
                    </CollectionHeaderMetadata>
                    {(!!(collection?.description || "").length || editMode) && (
                        <VerticalSpacing top={13}>
                            <UserThemableCollectionDescription as="div" $colorVariant={colorVariant} $customBackgroundColor={color}>
                                <GenericContentEditable
                                    colorVariant={colorVariant}
                                    text={collection?.description || ""}
                                    editMode={editMode}
                                    onEditCallback={onCollectionDescriptionEditEnd}
                                    placeholder="Enter description"
                                    customBackgroundColor={color}
                                />
                            </UserThemableCollectionDescription>
                        </VerticalSpacing>
                    )}
                    {editMode && (
                        <SidebarLayoutShowDesktop>
                            <CollectionEditCoverPos>
                                {
                                    //@ts-ignore
                                    collection?.ignoreCoverUpdated ? (
                                        <>
                                            {" "}
                                            <V3LoaderSpinner />{" "}
                                        </>
                                    ) : (
                                        <>
                                            <div>
                                                <V3TertiaryButton
                                                    onClick={() => onEditCoverClick && onEditCoverClick()}
                                                    text={collection.coverStyle !== UserCoverStyle.none ? "Edit cover" : "Add cover"}
                                                    skipSVGColor
                                                    ctaColor={hasImage ? V3TertiaryCTAColor.white : V3TertiaryCTAColor.color}
                                                    icon={<V3IconImage stroke={hasImage ? theme.v3.colors.white : theme.v3.colors.grey70} />}
                                                />
                                            </div>
                                            <div>
                                                {collection.coverStyle !== UserCoverStyle.none && (
                                                    <div>
                                                        <V3TertiaryButton
                                                            skipSVGColor
                                                            ctaColor={hasImage ? V3TertiaryCTAColor.white : V3TertiaryCTAColor.color}
                                                            onClick={() => {
                                                                onCoverModalsubmit && onCoverModalsubmit({ style: UserCoverStyle.none, color: "" });
                                                            }}
                                                            icon={<V3IconBin stroke={hasImage ? theme.v3.colors.white : theme.v3.colors.grey70} />}
                                                            text="Remove cover"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )
                                }
                            </CollectionEditCoverPos>
                        </SidebarLayoutShowDesktop>
                    )}
                </CollectionHeaderContent>
            </V4PHImageContainer>
        </>
    );
};
